/**
 * components/pdf/PdfBearbeiter – PDF bearbeiten als Vollbild-Fenster (Welle 136, für die KRV-Seite).
 *
 * Gleiche Werkzeuge wie „Dokument bearbeiten“ in Verschieben (Text, Stempel, Haken, Kreuz, Stift, Kasten,
 * Marker, Linie, Abdecken) plus „Seite löschen“ und „Mit anderem Dokument vereinen“. Gespeichert wird über
 * denselben Endpunkt /zuordnen/api/verschieben/pdf-bearbeiten — das Original in Dropbox wird ersetzt.
 * Was beim Vereinen mit dem zweiten Dokument passiert, entscheidet der Aufrufer (onVereinen).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FaChevronLeft, FaChevronRight, FaObjectGroup, FaSave, FaSync, FaTimes, FaTrash, FaUndo } from "react-icons/fa";
import { API_BASE } from "../../api/config";
import PdfEditLayer, { EDIT_FARBEN, EDIT_TOOLS } from "./PdfEditLayer";
import "../../pages/VerschiebenPage.css";
import "./PdfBearbeiter.css";

let _pdfjs = null;
async function ladePdfjs() {
  if (_pdfjs) return _pdfjs;
  let lib;
  try { lib = await import("pdfjs-dist/legacy/build/pdf.mjs"); } catch {
    try { lib = await import("pdfjs-dist/build/pdf.mjs"); } catch { lib = await import("pdfjs-dist"); }
  }
  const ver = lib?.version || "4.10.38";
  lib.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${ver}/legacy/build/pdf.worker.min.mjs`;
  _pdfjs = lib;
  return lib;
}

async function post(pfad, body) {
  const r = await fetch(API_BASE + "/zuordnen/api" + pfad, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  if (!r.ok) {
    let d = ""; try { d = (await r.json()).detail || ""; } catch { /* kein JSON */ }
    throw new Error(d || `Fehler ${r.status}`);
  }
  return r.json();
}

export default function PdfBearbeiter({ pfad, titel, kandidaten = [], onVereinen, onGespeichert, onSchliessen }) {
  const [doc, setDoc] = useState(null);
  const [seite, setSeite] = useState(0);
  const [vp, setVp] = useState(null);
  const [fehler, setFehler] = useState("");
  const [laden, setLaden] = useState(0);              // Zähler erzwingt Neuladen nach dem Vereinen
  const [tool, setTool] = useState("stempel");
  const [annos, setAnnos] = useState([]);
  const [felder, setFelder] = useState({});
  const [groesse, setGroesse] = useState(12);
  const [farbe, setFarbe] = useState(EDIT_FARBEN[1] || "#c62828");
  const [aktiveId, setAktiveId] = useState(null);
  const [loeschen, setLoeschen] = useState([]);
  const [speichert, setSpeichert] = useState(false);
  const [vereinenOffen, setVereinenOffen] = useState(false);
  const [vereinenMit, setVereinenMit] = useState(null);
  const [vereinenLaeuft, setVereinenLaeuft] = useState(false);
  const [vereinenSuche, setVereinenSuche] = useState("");
  const [frage, setFrage] = useState(null);           // Rückfrage im Fenster (window.confirm geht hier nicht)
  const canvasRef = useRef(null);
  const flaecheRef = useRef(null);

  const geaendert = annos.length > 0 || loeschen.length > 0;

  useEffect(() => {
    let abbruch = false;
    setDoc(null); setFehler("");
    (async () => {
      try {
        const lib = await ladePdfjs();
        const r = await fetch(API_BASE + "/zuordnen/api/verschieben/pdf-download?path=" + encodeURIComponent(pfad));
        if (!r.ok) throw new Error(`PDF nicht ladbar (${r.status})`);
        const d = await lib.getDocument({ data: await r.arrayBuffer() }).promise;
        if (!abbruch) { setDoc(d); setSeite((s) => Math.min(s, d.numPages - 1)); }
      } catch (e) { if (!abbruch) setFehler(String(e.message || e)); }
    })();
    return () => { abbruch = true; };
  }, [pfad, laden]);

  useEffect(() => {
    if (!doc || !canvasRef.current) return;
    let abbruch = false, task = null;
    (async () => {
      const pg = await doc.getPage(seite + 1);
      const canvas = canvasRef.current;
      if (!canvas || abbruch) return;
      const breite = (flaecheRef.current?.clientWidth || 800) - 40;
      const basis = pg.getViewport({ scale: 1 });
      const v = pg.getViewport({ scale: Math.min(2.2, Math.max(0.4, breite / basis.width)) });
      const dpr = window.devicePixelRatio || 1;
      canvas.style.width = Math.floor(v.width) + "px";
      canvas.style.height = Math.floor(v.height) + "px";
      canvas.width = Math.floor(v.width * dpr);
      canvas.height = Math.floor(v.height * dpr);
      const ctx = canvas.getContext("2d");
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      setVp(v);
      task = pg.render({ canvasContext: ctx, viewport: v });
      try { await task.promise; } catch { /* abgebrochen */ }
    })();
    return () => { abbruch = true; try { task && task.cancel(); } catch { /* egal */ } };
  }, [doc, seite]);

  const schliessen = useCallback(() => {
    if (geaendert) { setFrage({ text: "Änderungen verwerfen?", ja: () => onSchliessen?.() }); return; }
    onSchliessen?.();
  }, [geaendert, onSchliessen]);

  const speichern = async () => {
    setSpeichert(true); setFehler("");
    try {
      const annotationen = annos.filter((a) => a.typ !== "text" || (a.text || "").trim()).map(({ id, ...rest }) => rest);
      await post("/verschieben/pdf-bearbeiten", { pfad, annotationen, felder: {}, seiten_loeschen: loeschen });
      setAnnos([]); setLoeschen([]); setAktiveId(null); setSeite(0);
      onGespeichert?.();
      onSchliessen?.();
    } catch (e) { setFehler("Speichern fehlgeschlagen: " + (e.message || e)); }
    finally { setSpeichert(false); }
  };

  const vereinen = async () => {
    if (!vereinenMit) return;
    setVereinenLaeuft(true); setFehler("");
    try {
      await onVereinen?.(vereinenMit);
      setVereinenOffen(false); setVereinenMit(null);
      setLaden((n) => n + 1);               // vereinte Fassung neu laden
    } catch (e) { setFehler("Vereinen fehlgeschlagen: " + (e.message || e)); }
    finally { setVereinenLaeuft(false); }
  };

  const seiten = doc?.numPages || 0;
  const markiert = loeschen.includes(seite);

  return (
    <div className="v-pipeline-overlay" style={{ zIndex: 2000 }} onClick={schliessen}>
      <div className="pdfb-fenster" onClick={(e) => e.stopPropagation()}>
        <div className="pdfb-kopf">
          <b className="pdfb-titel" title={titel}>{titel}</b>
          <button className="v-pdf-tool-btn" title="Schließen" onClick={schliessen}><FaTimes /></button>
        </div>
        {doc && (
          <div className="v-pdf-toolbar v-edit-toolbar">
            <button className="v-pdf-tool-btn" title="Vorherige Seite" disabled={seite <= 0}
              onClick={() => setSeite((s) => s - 1)}><FaChevronLeft /></button>
            <span className="v-pdf-page-label">Seite {seite + 1} / {seiten}</span>
            <button className="v-pdf-tool-btn" title="Nächste Seite" disabled={seite >= seiten - 1}
              onClick={() => setSeite((s) => s + 1)}><FaChevronRight /></button>
            <span className="v-pdf-sep">|</span>
            {EDIT_TOOLS.map(({ key, Icon, title }) => (
              <button key={key} title={title} className={"v-pdf-tool-btn v-edit-tool" + (tool === key ? " aktiv" : "")}
                onClick={() => { setTool(key); setAktiveId(null); }}><Icon /></button>
            ))}
            <span className="v-pdf-sep">|</span>
            <select className="v-edit-select" title="Schriftgröße" value={groesse}
              onChange={(e) => setGroesse(Number(e.target.value))}>
              {[8, 10, 12, 14, 18, 24].map((g) => <option key={g} value={g}>{g}</option>)}
            </select>
            {EDIT_FARBEN.map((f) => (
              <button key={f} className={"v-edit-farbe" + (farbe === f ? " aktiv" : "")} style={{ background: f }}
                title={`Farbe ${f}`} onClick={() => setFarbe(f)} />
            ))}
            <button className="v-pdf-tool-btn" title="Letzte Änderung zurücknehmen" disabled={!annos.length}
              onClick={() => setAnnos((p) => p.slice(0, -1))}><FaUndo /></button>
            <span className="v-pdf-sep">|</span>
            <button className={"v-pdf-tool-btn v-edit-seite-weg" + (markiert ? " aktiv" : "")}
              title={markiert ? "Seite doch behalten" : "Diese Seite löschen (wirkt beim Speichern)"}
              disabled={!markiert && loeschen.length >= seiten - 1}
              onClick={() => setLoeschen((p) => p.includes(seite) ? p.filter((x) => x !== seite) : [...p, seite].sort((a, b) => a - b))}>
              <FaTrash />{loeschen.length > 0 && <span className="v-edit-zahl">{loeschen.length}</span>}
            </button>
            {onVereinen && (
              <button className="v-pdf-tool-btn v-edit-vereinen" title="Mit anderem Dokument vereinen (Seiten hinten anhängen)"
                onClick={() => {
                  if (geaendert) { setFehler("Bitte erst speichern oder die Änderungen zurücknehmen, dann vereinen."); return; }
                  setVereinenMit(null); setVereinenSuche(""); setVereinenOffen(true);
                }}><FaObjectGroup /></button>
            )}
            <span style={{ flex: 1 }} />
            <button className="v-pdf-tool-btn v-edit-save" disabled={speichert || !geaendert}
              title="Speichern – das Original in Dropbox wird ersetzt" onClick={speichern}>
              {speichert ? <FaSync className="spin" /> : <FaSave />}</button>
            <button className="v-pdf-tool-btn v-edit-cancel" title="Abbrechen" onClick={schliessen}><FaTimes /></button>
          </div>
        )}
        {fehler && <div className="pdfb-fehler">{fehler}</div>}
        {frage && (
          <div className="pdfb-frage">
            <span>{frage.text}</span>
            <button className="v-btn" onClick={() => { const f = frage; setFrage(null); f.ja(); }}>Ja</button>
            <button className="v-btn ghost" onClick={() => setFrage(null)}>Nein</button>
          </div>
        )}
        <div className="pdfb-flaeche" ref={flaecheRef}>
          {!doc && !fehler && <div style={{ padding: 30, opacity: 0.7 }}>Lade PDF …</div>}
          {doc && (
            <div className="pdfb-seite">
              <canvas ref={canvasRef} />
              {vp && (
                <PdfEditLayer vp={vp} seite={seite} tool={tool} annos={annos} setAnnos={setAnnos}
                  felder={felder} setFelder={setFelder} groesse={groesse} farbe={farbe}
                  aktiveId={aktiveId} setAktiveId={setAktiveId} />
              )}
              {markiert && <div className="v-edit-seite-weg-hinweis">Seite {seite + 1} wird beim Speichern gelöscht</div>}
            </div>
          )}
        </div>
        {vereinenOffen && (
          <div className="pdfb-vereinen">
            <h3 style={{ marginTop: 0 }}>Mit anderem Dokument vereinen</h3>
            {!vereinenMit ? (
              <>
                <input className="v-vereinen-suche" autoFocus placeholder="Dokument suchen …" value={vereinenSuche}
                  onChange={(e) => setVereinenSuche(e.target.value)} />
                <div className="v-vereinen-liste">
                  {kandidaten.length === 0 && <span style={{ opacity: 0.7 }}>Keine anderen Dokumente zur Auswahl.</span>}
                  {kandidaten.filter((k) => (k.name || "").toLowerCase().includes(vereinenSuche.trim().toLowerCase()))
                    .slice(0, 200).map((k) => (
                      <button key={k.key} className="v-vereinen-eintrag" onClick={() => setVereinenMit(k)}>{k.name}</button>
                    ))}
                </div>
              </>
            ) : (
              <div className="v-vereinen-frage">
                <p><b>{vereinenMit.name}</b> hinten an dieses Dokument anhängen?</p>
                {vereinenMit.hinweis && <p style={{ opacity: 0.8, fontSize: 13 }}>{vereinenMit.hinweis}</p>}
                <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                  <button className="v-btn" disabled={vereinenLaeuft} onClick={vereinen}>
                    {vereinenLaeuft ? "Vereint …" : "Vereinen"}</button>
                  <button className="v-btn ghost" disabled={vereinenLaeuft} onClick={() => setVereinenMit(null)}>Anderes wählen</button>
                </div>
              </div>
            )}
            <button className="v-pipeline-cancel" disabled={vereinenLaeuft} onClick={() => setVereinenOffen(false)}>Abbrechen</button>
          </div>
        )}
      </div>
    </div>
  );
}
